import type { ReactElement } from 'react';
import { Navigate, type RouteObject } from 'react-router';

import type { Permission } from '../api/types';
import { InvitationPage } from '../auth/InvitationPage';
import { LoginPage } from '../auth/LoginPage';
import { RequireAuth } from '../auth/RequireAuth';
import { NotFoundPage, RouteError } from '../components/RouteError';
import { OwnerPage } from '../owners/OwnerPage';
import { AcquisitionPage } from '../pages/AcquisitionPage';
import { AIReviewPage } from '../pages/AIReviewPage';
import { AppointmentDetailPage } from '../pages/AppointmentDetailPage';
import { BuyerRequestsPage } from '../pages/BuyerRequestsPage';
import { AppointmentsHub } from '../pages/AppointmentsHub';
import { AreaDetailPage } from '../pages/areas/AreaDetailPage';
import { AreasHub } from '../pages/areas/AreasHub';
import { GeoReviewPage } from '../pages/GeoReviewPage';
import { HomePage } from '../pages/HomePage';
import { LeadDetailPage } from '../pages/LeadDetailPage';
import { LeadsHub } from '../pages/LeadsHub';
import { MaklerPage } from '../pages/MaklerPage';
import { ProfilePage } from '../pages/ProfilePage';
import { TicketDetailPage, TicketsPage } from '../pages/TicketsPage';
import { ProjectLayout, ProjectRedirect } from '../projects/ProjectLayout';
import { RequirePermission } from '../projects/RequirePermission';
import { SignPage } from '../signatures/SignPage';
import { FollowUpsRoute, InboxRoute, Moved } from './routeHelpers';

const guarded = (permission: Permission, element: ReactElement) => <RequirePermission permission={permission}>{element}</RequirePermission>;

// Projektbezogene Seiten liegen unter /p/:projectKey – analog zur API (/projects/{projectId}/…).
export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },
  // Unterschreiben ohne Anmeldung (Link aus der E-Mail bzw. auf dem Gerät des Maklers).
  { path: '/unterschrift/:token', element: <SignPage />, errorElement: <RouteError /> },
  { path: '/einladung/:token', element: <InvitationPage />, errorElement: <RouteError /> },
  // Eigenes Termintool: Buchen ohne Anmeldung (Link aus Partner-Mails, Website) und Verwalten über den Link aus der Bestätigung.
  { path: '/termin/:slug', lazy: () => import('../booking/BookingPage').then(({ BookingPage }) => ({ element: <BookingPage /> })), errorElement: <RouteError /> },
  { path: '/termin/verwalten/:token', lazy: () => import('../booking/ManageBookingPage').then(({ ManageBookingPage }) => ({ element: <ManageBookingPage /> })), errorElement: <RouteError /> },
  {
    element: <RequireAuth />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <ProjectRedirect /> },
      { path: 'profil', element: <ProjectRedirect section="profil" /> },
      {
        path: 'p/:projectKey',
        element: <ProjectLayout />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'akquise', element: guarded('call.start', <AcquisitionPage />) },
          { path: 'akquise/:leadId', element: guarded('call.start', <AcquisitionPage />) },
          { path: 'posteingang', element: <InboxRoute /> },
          // Englische Kurzform in Links aus Benachrichtigungen (13.10.2026 führte „/inbox“ ins Leere).
          { path: 'inbox', element: <Navigate to="../posteingang" replace /> },
          { path: 'leads', element: guarded('lead.read', <LeadsHub />) },
          { path: 'kaufgesuche', element: guarded('lead.read', <BuyerRequestsPage />) },
          {
            path: 'partner',
            lazy: () => import('../pages/PartnerPipelinePage').then(({ PartnerPipelinePage }) => ({ element: guarded('area.read', <PartnerPipelinePage />) })),
          },
          { path: 'leads/:leadId', element: guarded('lead.read', <LeadDetailPage />) },
          { path: 'eigentuemer/:ownerId', element: guarded('lead.read', <OwnerPage />) },
          {
            path: 'leads/:leadId/unterlagen',
            lazy: () =>
              import('../pages/DocumentRequestPage').then(({ DocumentRequestPage }) => ({ element: guarded('document.read', <DocumentRequestPage />) })),
          },
          { path: 'termine', element: guarded('appointment.read', <AppointmentsHub />) },
          { path: 'termine/:appointmentId', element: guarded('appointment.read', <AppointmentDetailPage />) },
          {
            path: 'expose/:exposeId',
            lazy: () => import('../pages/ExposeEditorPage').then(({ ExposeEditorPage }) => ({ element: guarded('document.read', <ExposeEditorPage />) })),
          },
          {
            path: 'dokumente/:documentId',
            lazy: () => import('../pages/DocumentEditorPage').then(({ DocumentEditorPage }) => ({ element: guarded('document.read', <DocumentEditorPage />) })),
          },
          { path: 'gebiete', element: guarded('area.read', <AreasHub />) },
          { path: 'gebiete/:areaId', element: guarded('area.read', <AreaDetailPage />) },
          { path: 'makler/:userId', element: guarded('area.read', <MaklerPage />) },
          { path: 'tickets', element: guarded('ticket.read', <TicketsPage />) },
          { path: 'tickets/:ticketId', element: guarded('ticket.read', <TicketDetailPage />) },
          // Frühere Einzelseiten (Links in E-Mails und Lesezeichen bleiben gültig).
          { path: 'aufgaben', element: guarded('task.read', <FollowUpsRoute />) },
          { path: 'support', element: guarded('ticket.manage', <Moved to="posteingang" view="rueckfragen" />) },
          { path: 'ki-pruefung', element: guarded('ai.review', <AIReviewPage />) },
          {
            path: 'gespraeche',
            lazy: () => import('../pages/ConversationsPage').then(({ ConversationsPage }) => ({ element: guarded('call.review', <ConversationsPage />) })),
          },
          { path: 'geo/klaerung', element: guarded('lead.geo_review', <GeoReviewPage />) },
          { path: 'geo', element: guarded('lead.read', <Moved to="leads" view="regionen" />) },
          { path: 'karte', element: guarded('area.read', <Moved to="leads" view="karte" />) },
          { path: 'kalender', element: guarded('calendar.read', <Moved to="termine" view="kalender" />) },
          { path: 'aussendienst', element: guarded('appointment.read', <Moved to="termine" view="meine" />) },
          {
            path: 'vertrieb',
            lazy: () => import('../sales/SalesPage').then(({ SalesPage }) => ({ element: guarded('sales.read_own', <SalesPage />) })),
          },
          {
            path: 'rechnungen',
            lazy: () => import('../billing/InvoicesPage').then(({ InvoicesPage }) => ({ element: guarded('project.manage', <InvoicesPage />) })),
          },
          {
            path: 'rechnungen/:invoiceId',
            lazy: () =>
              import('../billing/InvoiceDetailPage').then(({ InvoiceDetailPage }) => ({ element: guarded('project.manage', <InvoiceDetailPage />) })),
          },
          {
            path: 'preisliste',
            lazy: () => import('../pricing/PriceListPage').then(({ PriceListPage }) => ({ element: <PriceListPage /> })),
          },
          {
            path: 'verwaltung',
            lazy: () => import('../pages/AdminPage').then(({ AdminPage }) => ({ element: guarded('project.read', <AdminPage />) })),
          },
          { path: 'profil', element: <ProfilePage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
];
